import type { ClaimCandidateStatus } from "@hp/types";
import { OVERVIEW_ONLY } from "./demoMode";

export type WorkflowButtonVariant = "primary" | "outline" | "subtle" | "danger";

/** Which action set to show in a report table or detail view. */
export type ClaimActionPreset = "review" | "pipeline" | "recovered";

export interface ClaimWorkflowAction {
  label: string;
  targetStatus: ClaimCandidateStatus;
  /**
   * Statuses to pass through, in order, before `targetStatus`, for moves the
   * backend state machine has no direct edge for (e.g. potential -> recovered
   * goes via validated). Only used by the three-status demo mode.
   */
  via?: ClaimCandidateStatus[];
  variant: WorkflowButtonVariant;
  requiresReason?: boolean;
  /** When paired with requiresReason, the modal's comment can be left blank. */
  reasonOptional?: boolean;
  reasonTitle?: string;
  reasonDescription?: string;
  confirmLabel?: string;
  confirmVariant?: "primary" | "danger";
}

const TO_POTENTIAL: ClaimWorkflowAction = {
  label: "To Potential",
  targetStatus: "identified",
  variant: "outline",
};

const MARK_RECOVERED: ClaimWorkflowAction = {
  label: "Mark Recovered",
  targetStatus: "recovered",
  variant: "primary",
  requiresReason: true,
  reasonOptional: true,
  reasonTitle: "Mark claim recovered",
  reasonDescription: "This claim will be marked recovered. Add a note on how/why, if useful for the record.",
  confirmLabel: "Mark Recovered",
  confirmVariant: "primary",
};

const REJECT: ClaimWorkflowAction = {
  label: "Reject",
  targetStatus: "rejected",
  variant: "danger",
  requiresReason: true,
  reasonTitle: "Reject claim candidate",
  reasonDescription:
    "This claim will be marked rejected and removed from active review. Give a reason for the record.",
  confirmLabel: "Reject",
  confirmVariant: "danger",
};

/** Three pipeline stages shown in the dashboard: potential → in progress → recovered. */
const IN_PROGRESS_STATUSES: ClaimCandidateStatus[] = ["validated", "filed", "allowed", "deferred"];

export function isInProgressStatus(status: ClaimCandidateStatus): boolean {
  return IN_PROGRESS_STATUSES.includes(status);
}

/**
 * A claim's FY tranches are all one recoverable claim (see
 * `claimCandidateGroupKey`'s own docstring), never separate claims - so a
 * status-change action always reads as acting on "this claim", regardless
 * of how many financial years back it. No "move all N" bulk phrasing.
 */
export function workflowActionsForPreset(
  status: ClaimCandidateStatus,
  preset: ClaimActionPreset,
): ClaimWorkflowAction[] {
  if (OVERVIEW_ONLY) return threeStatusActions(status);

  if (preset === "review" && status === "identified") {
    return [
      {
        label: "Move to In Progress",
        targetStatus: "validated",
        variant: "primary",
        requiresReason: true,
        reasonOptional: true,
        reasonTitle: "Move to In Progress",
        reasonDescription: "This claim will move into the in-progress pipeline. Add a note, if useful for the record.",
        confirmLabel: "Move to In Progress",
        confirmVariant: "primary",
      },
      REJECT,
    ];
  }

  if (preset === "recovered" && status === "recovered") {
    return [TO_POTENTIAL];
  }

  if (preset === "pipeline" && isInProgressStatus(status)) {
    return [MARK_RECOVERED, TO_POTENTIAL, REJECT];
  }

  return [];
}

/**
 * Demo mode has only three states - Potential, Recovered, Rejected - and no
 * In Progress step, so a claim moves straight between them. The backend has
 * no direct potential -> recovered or recovered -> rejected edge, hence `via`.
 * A rejected claim can always be reopened to Potential.
 */
function threeStatusActions(status: ClaimCandidateStatus): ClaimWorkflowAction[] {
  if (status === "identified") {
    return [{ ...MARK_RECOVERED, via: ["validated"] }, REJECT];
  }
  if (status === "recovered") {
    return [TO_POTENTIAL, { ...REJECT, via: ["identified"] }];
  }
  if (status === "rejected") {
    return [TO_POTENTIAL];
  }
  if (isInProgressStatus(status)) {
    return [MARK_RECOVERED, TO_POTENTIAL, REJECT];
  }
  return [];
}
